import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { pb, currentUser } from './pb';
import type { Tenant } from './types';

const STORAGE_KEY = 'focus_admin_selected_tenant';
// Cache del record del tenant activo (con sus capabilities) para rehidratar
// síncrono en recargas y evitar el "flash" del menú — ver `cachedTenant`.
const TENANT_CACHE_KEY = 'focus_admin_tenant_record';

export type ViewMode = 'admin' | 'tenant';

// Paths que pertenecen al panel Superadmin (modo 'admin'). Cualquier otro path
// se considera modo 'tenant'. El viewMode se DERIVA del path actual — no se
// persiste — para evitar desincronización entre estado y URL.
const ADMIN_PATH_PREFIXES = [
  '/clientes',
  '/admins',
  '/planes',
  '/screenshots',
  '/automations',
  '/configuracion',
];

function isAdminPath(pathname: string): boolean {
  return ADMIN_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

interface TenantContextValue {
  /** Tenant ID activo. null mientras carga la lista para el superadmin. */
  selectedTenantId: string | null;
  /** Cambia el tenant activo. No-op si el usuario no es superadmin. */
  setSelectedTenantId: (id: string) => void;
  /** Lista completa de tenants visibles para el usuario. Vacío mientras carga. */
  tenants: Tenant[];
  /** Tenant record correspondiente al selectedTenantId. */
  selectedTenant: Tenant | null;
  /** True si el usuario logueado es superadmin (puede alternar tenants). */
  isSuperadmin: boolean;
  /** Estado de carga de la lista de tenants. */
  isLoading: boolean;
  /**
   * Modo de visualización para superadmin:
   * - 'admin': panel fleet (Tenants + Screenshots + futuras herramientas globales).
   * - 'tenant': operando un tenant (CRM/Leads/Mensajes/etc, con banner sticky).
   *
   * Para client_admin/sales_rep siempre es 'tenant' (no aplica admin mode).
   */
  viewMode: ViewMode;
  /** Entra al modo tenant (operar un cliente específico). Setea el tenant activo. */
  enterTenantView: (tenantId: string) => void;
  /** Vuelve al modo admin (panel fleet). Solo aplica si es superadmin. */
  backToAdminView: () => void;
}

const TenantContext = createContext<TenantContextValue | null>(null);

// Override del tenant activo para un subárbol del admin (ej. la página de
// detalle del cliente /clientes/$id). Deja que los hooks scopeados por contexto
// —usePipelines y demás en lib/queries— operen sobre un tenant específico SIN
// cambiar el tenant global seleccionado (localStorage/topbar). Default null = no
// hay override, se usa el selectedTenantId global.
const ActiveTenantOverride = createContext<string | null>(null);

export function ActiveTenantOverrideProvider({
  tenantId,
  children,
}: {
  tenantId: string | null;
  children: ReactNode;
}) {
  return (
    <ActiveTenantOverride.Provider value={tenantId}>
      {children}
    </ActiveTenantOverride.Provider>
  );
}

/**
 * Proveedor del contexto de tenant activo.
 *
 * - `client_admin` / `sales_rep`: el tenant queda fijo en `user.tenant`,
 *   viewMode siempre 'tenant'.
 * - `superadmin`: lee/escribe `selectedTenantId` + `viewMode` en localStorage.
 *   Por default arranca en modo 'admin' (panel fleet). El TenantSwitcher y la
 *   acción "Entrar" desde /tenants cambian el modo a 'tenant'.
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const user = currentUser();
  const isSuperadmin = user?.role === 'superadmin';

  // Para client_admin / sales_rep: el tenant es fijo (el suyo).
  // Para superadmin: leemos de localStorage o queda null hasta que carguen los tenants.
  const [selectedTenantId, setSelectedTenantIdState] = useState<string | null>(
    () => {
      if (!user) return null;
      if (!isSuperadmin) return user.tenant ?? null;
      try {
        return localStorage.getItem(STORAGE_KEY);
      } catch {
        return null;
      }
    },
  );

  // Record del tenant activo rehidratado del cache. El record "fresco" llega por
  // fetch async; mientras tanto este cache (si corresponde al tenant activo) hace
  // que el PRIMER render ya conozca las capabilities, así el menú se arma correcto
  // de entrada y no parpadea (mostrar todo → esconder). Se refresca con el fetch.
  const [cachedTenant, setCachedTenant] = useState<Tenant | null>(() => {
    try {
      const raw = localStorage.getItem(TENANT_CACHE_KEY);
      return raw ? (JSON.parse(raw) as Tenant) : null;
    } catch {
      return null;
    }
  });

  // viewMode se DERIVA del path actual. Si el usuario no es superadmin, siempre
  // es 'tenant' (no aplica modo admin para client_admin/sales_rep).
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const viewMode: ViewMode = !isSuperadmin
    ? 'tenant'
    : isAdminPath(pathname)
      ? 'admin'
      : 'tenant';

  const tenantsQuery = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      const res = await pb.collection('tenants').getList<Tenant>(1, 100, {
        filter: 'active = true',
        sort: 'name',
      });
      return res.items;
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  // Si el superadmin no tiene un tenant elegido y ya cargaron, usa el primero.
  useEffect(() => {
    if (!isSuperadmin) return;
    if (!tenantsQuery.data || tenantsQuery.data.length === 0) return;
    if (selectedTenantId) {
      // Si el guardado ya no existe (tenant borrado), reset al primero.
      const exists = tenantsQuery.data.some((t) => t.id === selectedTenantId);
      if (!exists) {
        setSelectedTenantIdState(tenantsQuery.data[0].id);
      }
      return;
    }
    setSelectedTenantIdState(tenantsQuery.data[0].id);
  }, [isSuperadmin, tenantsQuery.data, selectedTenantId]);

  const setSelectedTenantId = useCallback(
    (id: string) => {
      if (!isSuperadmin) return; // client_admin no puede cambiar
      setSelectedTenantIdState(id);
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch {
        /* ignore */
      }
    },
    [isSuperadmin],
  );

  // enterTenantView: setea el tenant activo (no cambia viewMode — eso se deriva
  // del path; el caller debe navegar a una ruta tenant después).
  const enterTenantView = useCallback(
    (tenantId: string) => {
      if (!isSuperadmin) return;
      setSelectedTenantIdState(tenantId);
      try {
        localStorage.setItem(STORAGE_KEY, tenantId);
      } catch {
        /* ignore */
      }
    },
    [isSuperadmin],
  );

  // backToAdminView: no-op (viewMode se deriva del path). El caller navega a
  // /tenants o /screenshots para entrar al modo admin. Mantenido como helper
  // por simetría con enterTenantView y para que el caller no tenga que conocer
  // la lista de admin paths.
  const backToAdminView = useCallback(() => {
    /* viewMode derivado del path — el caller navega a /tenants */
  }, []);

  const tenants = useMemo(() => tenantsQuery.data ?? [], [tenantsQuery.data]);
  const freshTenant = useMemo(
    () => tenants.find((t) => t.id === selectedTenantId) ?? null,
    [tenants, selectedTenantId],
  );
  // Usa el record fresco si ya llegó; si no, el cacheado SOLO si corresponde al
  // tenant activo (evita usar el cache de otro tenant tras un switch del superadmin).
  const selectedTenant =
    freshTenant ?? (cachedTenant?.id === selectedTenantId ? cachedTenant : null);

  // Persistir el record fresco al cache para la próxima recarga.
  useEffect(() => {
    if (!freshTenant) return;
    setCachedTenant(freshTenant);
    try {
      localStorage.setItem(TENANT_CACHE_KEY, JSON.stringify(freshTenant));
    } catch {
      /* ignore */
    }
  }, [freshTenant]);

  const value = useMemo<TenantContextValue>(() => {
    return {
      selectedTenantId,
      setSelectedTenantId,
      tenants,
      selectedTenant,
      isSuperadmin,
      isLoading: tenantsQuery.isLoading,
      viewMode,
      enterTenantView,
      backToAdminView,
    };
  }, [
    selectedTenantId,
    setSelectedTenantId,
    tenants,
    selectedTenant,
    isSuperadmin,
    tenantsQuery.isLoading,
    viewMode,
    enterTenantView,
    backToAdminView,
  ]);

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

export function useTenantContext(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error(
      'useTenantContext debe usarse dentro de <TenantProvider>',
    );
  }
  return ctx;
}

/** Atajo: el tenant ID activo (o null). Útil para queries. */
export function useActiveTenantId(): string | null {
  // El override (si existe) gana sobre el tenant global — ambos hooks se llaman
  // siempre (reglas de hooks). Fuera de un ActiveTenantOverrideProvider el
  // override es null y se usa el selectedTenantId global de siempre.
  const override = useContext(ActiveTenantOverride);
  const ctx = useTenantContext();
  return override ?? ctx.selectedTenantId;
}
